import React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { usersReducer } from './reducer';
import { selectSearchEmail, selectSort, selectUsers } from './selectors';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import useIsAdminCompact from '../../hooks/useIsAdminCompact';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import { DatePicker, Select, Table, Button, Card, Input } from 'antd';
import type { TablePaginationConfig } from 'antd';
import type { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';
import type { UserDto as User } from '@poker-stats/contracts';

const subscriptionOptions = [
  { label: '10-25', value: 1 },
  { label: '10-50', value: 2 },
  { label: '10-200', value: 3 },
  { label: 'Unlim', value: 4 },
];

const pokersiteOptions = [
  { label: 'PS', value: 1 },
  { label: 'CHI', value: 2 },
  { label: 'WPN', value: 3 },
  { label: '888', value: 4 },
  { label: 'CPR', value: 5 },
  { label: 'IP', value: 6 },
];

type UsersPageProps = {
  embedded?: boolean;
  userLinkBase?: string;
};

const UsersPage = React.memo(({ embedded = false, userLinkBase = '/users' }: UsersPageProps) => {
  useIsAdmin();
  const isCompact = useIsAdminCompact();

  const dispatch = useDispatch();
  const users = useSelector(selectUsers);
  const searchEmail = useSelector(selectSearchEmail);
  const sort = useSelector(selectSort);

  React.useEffect(() => {
    dispatch(usersReducer.actions.loadUsers());
  }, [dispatch]);

  const handleSeacth = React.useCallback(() => {
    dispatch(usersReducer.actions.loadUsers());
  }, [dispatch]);

  const handleActivateUser = React.useCallback(
    (id: number) => {
      dispatch(usersReducer.actions.activateUser(id));
    },
    [dispatch],
  );

  const handleChangeEmail = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(usersReducer.actions.changeEmail(e.target.value));
    },
    [dispatch],
  );

  const handleChangeAvailablePokersiteIds = React.useCallback(
    (id: number, value: Array<number>) => {
      dispatch(
        usersReducer.actions.changeUserAvailablePokersiteIds({
          id,
          availablePokersiteIds: value,
        }),
      );
    },
    [dispatch],
  );

  const handleChangeUserSubscription = React.useCallback(
    (id: number, value: number) => {
      dispatch(usersReducer.actions.changeUserSubscription({ id, subscription: value }));
    },
    [dispatch],
  );

  const handleChangeUserSubscriptionDate = React.useCallback(
    (id: number, value: dayjs.Dayjs | null) => {
      dispatch(
        usersReducer.actions.changeUserSubscriptionDate({
          id,
          subscriptionDate: value ? value.format('YYYY-MM-DD') : undefined,
        }),
      );
    },
    [dispatch],
  );

  const handleChangeTable = React.useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<User> | SorterResult<User>[],
      _extra: TableCurrentDataSource<User>,
    ) => {
      const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      dispatch(
        usersReducer.actions.changeTable({
          sortKey: String(activeSorter?.columnKey ?? ''),
          sortOrder: activeSorter?.order ?? '',
        }),
      );
      dispatch(usersReducer.actions.loadUsers());
    },
    [dispatch],
  );

  const columns = React.useMemo(
    () => [
      {
        key: 'id',
        title: 'Id',
        dataIndex: 'id',
        width: 64,
        sorter: true,
        sortOrder:
          sort && sort.sortKey === 'id' ? (sort.sortOrder as 'ascend' | 'descend' | null) : null,
      },
      {
        key: 'email',
        title: 'Email',
        render: (x: User) => (
          <Link className="v2-admin-user-link" to={`${userLinkBase}/${x.id}`}>
            {x.email}
          </Link>
        ),
        sorter: true,
        sortOrder:
          sort && sort.sortKey === 'email' ? (sort.sortOrder as 'ascend' | 'descend' | null) : null,
      },
      {
        key: 'isConfirmed',
        title: 'Подтверждён',
        width: 110,
        render: (x: User) => (
          <span className={x.isConfirmed ? 'v2-admin-badge v2-admin-badge--ok' : 'v2-admin-badge'}>
            {x.isConfirmed ? 'Да' : 'Нет'}
          </span>
        ),
        sorter: true,
        sortOrder:
          sort && sort.sortKey === 'isConfirmed'
            ? (sort.sortOrder as 'ascend' | 'descend' | null)
            : null,
      },
      {
        key: 'isAdmin',
        title: 'Админ',
        width: 88,
        render: (x: User) => (x.isAdmin ? 'Да' : '—'),
        sorter: true,
        sortOrder:
          sort && sort.sortKey === 'isAdmin'
            ? (sort.sortOrder as 'ascend' | 'descend' | null)
            : null,
      },
      {
        key: 'last_search_date',
        title: 'Последний поиск',
        width: 150,
        render: (x: User) =>
          x.last_search_date ? dayjs(x.last_search_date).format('DD.MM.YY HH:mm') : '—',
        sorter: true,
        sortOrder:
          sort && sort.sortKey === 'last_search_date'
            ? (sort.sortOrder as 'ascend' | 'descend' | null)
            : null,
      },
      {
        key: 'subscription',
        title: 'Подписка',
        width: 112,
        render: (x: User) => (
          <Select
            options={subscriptionOptions}
            allowClear
            value={x.subscription}
            onChange={(value) => handleChangeUserSubscription(x.id, value)}
            popupMatchSelectWidth={false}
          />
        ),
        sorter: true,
        sortOrder:
          sort && sort.sortKey === 'subscription'
            ? (sort.sortOrder as 'ascend' | 'descend' | null)
            : null,
      },
      {
        key: 'subscription_date',
        title: 'До',
        width: 148,
        render: (x: User) => (
          <DatePicker
            value={x.subscription_date ? dayjs(x.subscription_date) : undefined}
            onChange={(value) => handleChangeUserSubscriptionDate(x.id, value)}
            format="DD.MM.YYYY"
            allowClear
          />
        ),
        sorter: true,
        sortOrder:
          sort && sort.sortKey === 'subscription_date'
            ? (sort.sortOrder as 'ascend' | 'descend' | null)
            : null,
      },
      {
        key: 'available_pokersite_ids',
        title: 'Румы',
        width: 180,
        render: (x: User) => (
          <Select
            mode="multiple"
            options={pokersiteOptions}
            value={x.available_pokersite_ids}
            onChange={(value) => handleChangeAvailablePokersiteIds(x.id, value)}
            maxTagCount="responsive"
            popupMatchSelectWidth={false}
          />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 120,
        render: (x: User) =>
          !x.isConfirmed ? (
            <Button type="primary" size="small" onClick={() => handleActivateUser(x.id)}>
              Активировать
            </Button>
          ) : null,
      },
    ],
    [
      handleActivateUser,
      handleChangeAvailablePokersiteIds,
      handleChangeUserSubscription,
      handleChangeUserSubscriptionDate,
      sort,
      userLinkBase,
    ],
  );

  const mobileCards = React.useMemo(
    () =>
      users?.map((user) => (
        <li className="v2-admin-user-card" key={user.id}>
          <div className="v2-admin-user-card__head">
            <Link className="v2-admin-user-link" to={`${userLinkBase}/${user.id}`}>
              {user.email}
            </Link>
            <span className="v2-admin-user-card__id">#{user.id}</span>
          </div>
          <div className="v2-admin-user-card__badges">
            <span
              className={user.isConfirmed ? 'v2-admin-badge v2-admin-badge--ok' : 'v2-admin-badge'}
            >
              {user.isConfirmed ? 'Подтверждён' : 'Не подтверждён'}
            </span>
            {user.isAdmin ? (
              <span className="v2-admin-badge v2-admin-badge--admin">Админ</span>
            ) : null}
          </div>
          <p className="v2-admin-user-card__meta">
            Последний поиск:{' '}
            {user.last_search_date ? dayjs(user.last_search_date).format('DD.MM.YY HH:mm') : '—'}
          </p>
          <div className="v2-admin-user-card__fields">
            <label className="settings-field">
              <span className="settings-field-label">Подписка</span>
              <Select
                options={subscriptionOptions}
                allowClear
                value={user.subscription}
                onChange={(value) => handleChangeUserSubscription(user.id, value)}
              />
            </label>
            <label className="settings-field">
              <span className="settings-field-label">До</span>
              <DatePicker
                value={user.subscription_date ? dayjs(user.subscription_date) : undefined}
                onChange={(value) => handleChangeUserSubscriptionDate(user.id, value)}
                format="DD.MM.YYYY"
                allowClear
                style={{ width: '100%' }}
              />
            </label>
            <label className="settings-field v2-admin-user-card__rooms">
              <span className="settings-field-label">Румы</span>
              <Select
                mode="multiple"
                options={pokersiteOptions}
                value={user.available_pokersite_ids}
                onChange={(value) => handleChangeAvailablePokersiteIds(user.id, value)}
                maxTagCount="responsive"
              />
            </label>
          </div>
          {!user.isConfirmed ? (
            <button
              type="button"
              className="acid-button v2-admin-user-card__activate"
              onClick={() => handleActivateUser(user.id)}
            >
              Активировать
            </button>
          ) : null}
        </li>
      )) ?? null,
    [
      handleActivateUser,
      handleChangeAvailablePokersiteIds,
      handleChangeUserSubscription,
      handleChangeUserSubscriptionDate,
      userLinkBase,
      users,
    ],
  );

  if (!users) {
    return null;
  }

  const table = (
    <Table
      className="v2-admin-users-table"
      dataSource={users}
      rowKey="id"
      columns={columns}
      size="small"
      onChange={handleChangeTable}
      scroll={embedded && !isCompact ? { x: 980 } : undefined}
      pagination={{ pageSize: 20, showSizeChanger: false }}
    />
  );

  if (embedded) {
    return (
      <section className="settings-panel v2-admin-users-panel" aria-label="Список пользователей">
        <div className="settings-panel__head v2-admin-table-panel__head">
          <h2>Список пользователей</h2>
          <p className="v2-admin-table-panel__meta">
            Найдено: <strong>{users.length}</strong>
          </p>
        </div>
        <div className="v2-admin-users-toolbar">
          <Input.Search
            placeholder="Поиск по email"
            onChange={handleChangeEmail}
            onSearch={handleSeacth}
            value={searchEmail}
            allowClear
            className="v2-admin-users-search"
          />
        </div>
        {isCompact ? (
          <ul className="v2-admin-user-cards">{mobileCards}</ul>
        ) : (
          <div className="table-scroll v2-admin-table-scroll" tabIndex={0}>
            {table}
          </div>
        )}
      </section>
    );
  }

  return (
    <div style={{ margin: '32px auto', maxWidth: 1600 }}>
      <Card extra={<Link to="/admin/referrals">Рефералы</Link>}>
        <Table
          dataSource={users}
          rowKey="id"
          columns={columns}
          size="small"
          onChange={handleChangeTable}
          style={{ width: '100%' }}
          title={() => (
            <Input.Search
              placeholder="Email"
              onChange={handleChangeEmail}
              onSearch={handleSeacth}
              style={{ width: 250 }}
              value={searchEmail}
            />
          )}
        />
      </Card>
    </div>
  );
});

export default UsersPage;
